import { resolveCardRating } from './card-rating'
import { useCallback, useEffect, useMemo, useState } from "react"
import { readLabelConfig, collectCardLabels, partitionCardLabels, overlayGroupStyle, iconMaskStyle, LABEL_OVERLAY_DOTNAV_RESERVE, readCardTagsConfig, collectCardTags, cardTagPillStyle, type CardLabel } from './label-style'
import { useDevicePixelScale } from '../use-device-pixel-scale'
import type { ExploreProperty, QuoteRateplan } from "../../widgets/explorer/data"
import { getProxiedImageUrl } from "../image-proxy"
import { writePartyParams } from "../occupancy"
import type { ExploreWidgetConfig, VisitorFilter } from "../../widgets/explorer/config"
import { isFavorite, toggleFavorite } from "./favorites"
import type { MapActions } from "../../widgets/explorer/providers"
import { useWidget } from "../../providers/widget"
import { useFetchWidget } from "../../hooks"
import { formatPrice as formatCardPrice } from "./card-parts"
import { cardNoDatesLabel, rateUnbookable } from './no-dates-label'
import { isRoundingStrikeWorthShowing, originalTotalOf } from '../rounding-strike-guard'
import { pickUnitGroupSlug } from './unit-group'
import { CompareMark, compareRootProps, type CardCompare } from './card-compare'
import { propertyLinkRel, resolvePropertyLink, withStayParams } from '../property-href'
import { usePropertyHrefFeed } from '../use-property-href-feed'
import { CardTierBadge } from './TierBadge'
import { dotWindow } from './CardMedia'

/**
 * Slide dots shown at once (#1865). Each dot is a 24px touch target, so a long
 * photo set would wrap into rows over the photo and under the bottom overlay
 * badges; past this many the dots follow the active slide in a window, like
 * the Template card's. 7 is the default photosLimit, so a default card still
 * shows every dot.
 */
const MAX_DOTS = 7

interface PropertyCardProps {
  property: ExploreProperty
  options: ExploreWidgetConfig
  visitorFilters?: VisitorFilter
  quoteData?: { rateplan: QuoteRateplan; nights: number } | null
  quoteLoading?: boolean
  quoteFailed?: boolean
  showFavoriteButton?: boolean
  mapActions?: MapActions | null
  /** Compare Homes (#1713): set only while compare mode is on. */
  compare?: CardCompare | null
}

/**
 * The explorer-look card, moved here verbatim from
 * widgets/explorer/components/PropertyCard.tsx so every card renderer lives in
 * one module. Markup is intentionally untouched: the static explorer-card CSS
 * and the #895 card-skin generator (common/property-card/card-skin-css.ts)
 * both target these .hfse-property-* / .hk-* class names.
 * `formatPrice` comes from card-parts — locale pinned to 'en-US', currency from
 * the property (#1489) — fixing
 * a latent SSR/hydration locale mismatch the local copy here had.
 */
export default function TemplateExplorer({
  property,
  options,
  visitorFilters,
  quoteData,
  quoteLoading,
  quoteFailed,
  showFavoriteButton,
  mapActions,
  compare,
}: PropertyCardProps) {
  // Every price this card prints is in the property's own currency (#1489).
  // Bound once so each call site below reads exactly as it did.
  const formatPrice = (amount: number) => formatCardPrice(amount, property.currency)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  // Seed false (matches SSR, where localStorage is unavailable) so the client's
  // first render matches the server; the effect below reads the real favorite
  // state on mount. Reading localStorage in the initializer would diverge an
  // already-favorited card's className/aria-label/svg from the server (#771).
  const [favorited, setFavorited] = useState(false)
  const card = options.card ?? {
    openInNewTab: false,
    showLocation: true,
    showType: true,
    showFeatures: true,
    showRating: false,
    photosLimit: 7,
    imageAspectRatio: '16/9' as const,
  }
  // Card rating (toolkit card-rating parity): threshold + placement live in
  // card-rating.ts, shared with the map popup. No new leaves = the old gate.
  const cardRating = resolveCardRating(property.rating, card)
  const renderRating = (where: 'title' | 'location') => cardRating && cardRating.placement === where && (
    <span
      className={where === 'location' ? 'hfse-property-rating hfse-property-rating-location' : 'hfse-property-rating'}
      title={cardRating.title}
      {...(cardRating.ariaLabel ? { role: 'img', 'aria-label': cardRating.ariaLabel } : {})}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden={cardRating.ariaLabel ? 'true' : undefined}><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
      <span className="hfse-property-rating-avg">{cardRating.average}</span>
      <span className="hfse-property-rating-count">({cardRating.count})</span>
    </span>
  )
  const images = property.images.slice(0, card.photosLimit ?? 7)
  const hasMultipleImages = images.length > 1

  // Resolve a grid-badge taxonomy (WP: grid_left/right_taxonomy) to a joined term
  // string, capped at the configured limit. type/city are single-valued;
  // location/group come from the per-property term lists (central #2615).
  const resolveTaxonomy = (key: 'type' | 'city' | 'location' | 'group', limit: number): string => {
    let names: string[]
    switch (key) {
      case 'type': names = property.propertyType ? [property.propertyType] : []; break
      case 'city': names = property.location?.label ? [property.location.label] : []; break
      case 'location': names = (property.locations ?? []).map(l => l.name); break
      case 'group': names = (property.groups ?? []).map(g => g.name); break
      default: names = []
    }
    return names.filter(Boolean).slice(0, Math.max(1, limit)).join(' / ')
  }
  // Label Designer (#1069): null = no override rows = legacy badge branch.
  const labelConfig = readLabelConfig(card as any)
  const cardLabels = labelConfig ? collectCardLabels(property, labelConfig) : []
  // Placement (#1370 slice 1): overlay-placed badges leave the row and pin to
  // a photo corner inside the slideshow; without a photo they stay in the row.
  // Same badge markup either way — one renderer, so the row is byte-identical
  // to v1/v2 and skins targeting .hfse-property-group reach both.
  const { inline: inlineLabels, overlayGroups } = partitionCardLabels(cardLabels, images.length > 0)
  // Tier Rating (#1813): one badge per card, under the title or top-left over
  // the photo. Null config = no node at all, so the card stays byte-identical.
  const tierRating = (card as any).tierRating
  // Media placement joins the card's own labels row when there is one (the
  // toolkit's rule: the badge flows as its first item instead of overlaying
  // the same top-left spot); with no row it pins itself over the photo.
  const hasLabelsRow = card.showGroups !== false && (labelConfig ? inlineLabels.length > 0 : !!property.tag)
  const tierInLabelsRow = hasLabelsRow && images.length > 0
  const renderBadge = (label: CardLabel) => {
    // Icon recolor + icon-only (#1370 slice 2): a tinted icon is a span masked
    // by the .svg and filled with currentColor (explorer-card.css); the plain
    // icon is v1's <img>. Icon-only keeps the text in a visually hidden span so
    // the card link's accessible name is unchanged. No flags = v1 markup.
    const cls = label.styled ? 'hfse-property-group hfse-label-styled' : 'hfse-property-group'
    return (
      <span
        key={label.key}
        className={label.iconOnly ? `${cls} hfse-label-icon-only` : cls}
        style={label.css}
      >
        {label.iconUrl && (label.recolorIcon
          ? <span className="hfse-label-icon hfse-label-icon-tint" style={iconMaskStyle(label.iconUrl)} aria-hidden="true" />
          : <img className="hfse-label-icon" src={label.iconUrl} alt="" loading="lazy" />)}
        {label.iconOnly ? <span className="hfse-label-icon-only-text">{label.text}</span> : label.text}
      </span>
    )
  }
  const leftTerms = resolveTaxonomy((card as any).gridLeftTaxonomy ?? 'city', (card as any).gridLeftLimit ?? 5)
  const rightTerms = resolveTaxonomy((card as any).gridRightTaxonomy ?? 'type', (card as any).gridRightLimit ?? 5)
  // Card tags (#1812, toolkit CardTags): the home's listed tags, in the
  // operator's priority order, take the right (type) slot — the slot the
  // toolkit rewrites server-side. null = feature off = the slot is untouched.
  // A home with none of the listed tags keeps its type line unless keepType
  // is off, in which case the slot stays empty (CardTags::inject's
  // keep_type branch) — only when the slot really shows the TYPE: the
  // toolkit clears `types` alone, so a right slot mapped to city / location
  // / group is left as it is. The slot is still gated by showType, as the
  // toolkit's slot is by hide_types.
  const cardTagsCfg = readCardTagsConfig(card)
  const cardTags = cardTagsCfg ? collectCardTags(property, cardTagsCfg) : []
  const showTagSlot = card.showType !== false && cardTags.length > 0
  const rightSlotIsType = ((card as any).gridRightTaxonomy ?? 'type') === 'type'
  const dropEmptyTypeSlot = cardTagsCfg !== null && cardTagsCfg.keepType === false && rightSlotIsType
  const showTypeSlot = card.showType !== false && !showTagSlot && !!rightTerms && !dropEmptyTypeSlot
  const tagPills = cardTagsCfg !== null && cardTagsCfg.pills !== false
  const tagOwnRow = showTagSlot && cardTagsCfg !== null && cardTagsCfg.ownRow !== false
  const tagPillStyle = cardTagsCfg && tagPills ? cardTagPillStyle(cardTagsCfg) : undefined
  // Admin-selectable icomoon icons for the badge chips (#563, WP:
  // grid_left_icon / grid_right_icon); empty keeps the built-in SVGs.
  const leftIcon = (card as any).gridLeftIcon as string | undefined
  const rightIcon = (card as any).gridRightIcon as string | undefined

  // Card subtitle (WP: subtitle_source) — primary source, falling back to the alternate.
  const pickSubtitle = (src?: string): string =>
    src && src !== 'none' ? (property.subtitle?.[src as keyof NonNullable<typeof property.subtitle>] || '') : ''
  const subtitleText = pickSubtitle((card as any).subtitleSource) || pickSubtitle((card as any).alternateSubtitleSource)

  // Card-template flags whose effect is markup, not CSS (#872). Both are
  // neutral by default, so an unconfigured card renders the exact tree it
  // rendered before this group existed. The rest of the Skin card surface is
  // pure CSS — see ./card-skin-css.
  //
  // Text specs are a faithful port of the toolkit's homerunner-skin.js labels
  // ("Sleeps 4 | 2 BR | 1 BA"), except that the widget owns its own markup and
  // can render the labels directly instead of rewriting text nodes after paint.
  const specText = card.metaSpecStyle === 'text'
  const showTypeIcon = card.showTypeIcon !== false
  const bedsLabel = property.features.beds || 'Studio'

  // Slideshow transition per the card.slideAnimation setting (WP plugin parity).
  // Active slide is in normal flow (sizes the container); inactive slides overlay it.
  const slideAnimation = card.slideAnimation ?? 'fade'
  const slideStyle = (isActive: boolean): React.CSSProperties => {
    const base: React.CSSProperties = { position: isActive ? 'relative' : 'absolute', top: 0, left: 0, width: '100%', height: '100%' }
    if (slideAnimation === 'slide') {
      return { ...base, opacity: 1, transform: isActive ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.4s ease' }
    }
    if (slideAnimation === 'scale') {
      return { ...base, opacity: isActive ? 1 : 0, transform: isActive ? 'scale(1)' : 'scale(1.06)', transition: 'transform 0.5s ease, opacity 0.4s ease' }
    }
    return { ...base, opacity: isActive ? 1 : 0, transition: 'opacity 0.4s ease' }
  }

  // Request card images at the device pixel ratio (capped at 2x) so they stay
  // crisp on retina displays. At 1x the base 600×400 is unchanged; at 2x we
  // fetch 1200×800 — matching/exceeding the WP plugin's 900px medium images.
  // Hydration-safe: starts at the SSR value (2x) and refines post-mount, so
  // the img src can't mismatch on 1x displays and torch the SSR tree.
  const imgScale = useDevicePixelScale()
  const imgW = 600 * imgScale
  const imgH = 400 * imgScale

  const handlePrevImage = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  const handleFavoriteToggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const newState = toggleFavorite(property.id)
    setFavorited(newState)
  }, [property.id])

  // Sync favorite state on property change
  useEffect(() => {
    setFavorited(isFavorite(property.id))
  }, [property.id])

  const { feedId, widgetId } = useWidget()
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const pdpRoute = widgetData?.feed?.additional_info?.reverseProxy?.routes?.pdp || '/property/[slug]'
  const hrefFeed = usePropertyHrefFeed(widgetData?.feed)

  // Build property URL with filter params, for a given slug — extracted from
  // the plain propertyUrl memo below so the click handler can rebuild it for
  // a randomly-picked sibling slug (#1070) without duplicating the filter
  // logic. #1714: the base link comes from the shared resolver, so a property
  // the feed maps to its own page (External Property Pages) links there.
  const buildPropertyLink = useCallback((slug: string | null): { href: string; external: boolean } => {
    if (!slug) return { href: '#', external: false }
    const link = resolvePropertyLink({ feed: hrefFeed, slug, pdpRoute })
    if (!visitorFilters) return link

    const params = new URLSearchParams()
    if (visitorFilters.checkin && visitorFilters.checkout) {
      params.set('checkin', visitorFilters.checkin)
      params.set('checkout', visitorFilters.checkout)
    }
    // Carry the whole party, pets included. The pets flag used to stop here, which
    // meant a "Pets: Yes" search handed the visitor a picker at 0 pets (common/occupancy).
    writePartyParams(params, {
      adults: visitorFilters.adults,
      children: visitorFilters.children,
      pets: options.showPetsFilter === false ? undefined : visitorFilters.pets,
    })

    return { href: withStayParams(link, params.toString()), external: link.external }
  }, [hrefFeed, pdpRoute, visitorFilters, options.showPetsFilter])

  const propertyLink = useMemo(
    () => buildPropertyLink(property.slug),
    [buildPropertyLink, property.slug],
  )
  const propertyUrl = propertyLink.href

  // Unit Grouping (#1070, central#3210): on every click, re-resolve the target
  // to a uniformly random sibling (or the property's own slug when ungrouped
  // — pickUnitGroupSlug is a no-op there, so this is unconditionally safe).
  // Mutating the anchor's href synchronously inside the click handler, before
  // the browser processes the click's default navigation, is what makes each
  // click bind to a freshly-picked unit instead of the first one rendered.
  const handleCardClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.href = buildPropertyLink(pickUnitGroupSlug(property)).href
  }, [buildPropertyLink, property])

  // Build price display
  const isDateQuery = Boolean(visitorFilters?.checkin && visitorFilters?.checkout)
  const priceDisplayMode = isDateQuery ? options.priceDisplay.withDates : options.priceDisplay.noDates

  const renderPrice = () => {
    // Hidden by config
    if (priceDisplayMode === 'none') return null

    // Toolkit lowest-rate-availability: no bookable nights → label, not the base rate
    // (mirrors GridQuotePrice; the two trees must stay behaviorally identical).
    // Blank label = legacy fallback. With full-price quotes ON for a dated search the
    // quote queue is sequential, so reaching a fallback with no quoteData/loading/
    // failed means "still waiting", never "unavailable" — no label while pending.
    // Null too when Central flags the calendar stale or reports open nights
    // (central#3089): a rate nobody has vouched for is not "no dates".
    const unavailableLabel = cardNoDatesLabel(options, property)
    const quotePending = isDateQuery && !!options.fullPrice
    const renderUnavailable = () => (
      <div className="hk-margin-small-top hfse-property-price-container">
        <span className="hfse-property-price hfse-property-price-unavailable">{unavailableLabel}</span>
      </div>
    )

    // base / lowest don't need a quote — resolve straight from listing data so they render
    // consistently before OR after a date search (WP: base_rent / lowest_rent). `lowest`
    // prefers the date-scoped lowest, then the calendar-derived lowest, then the base rate.
    if (priceDisplayMode === 'base' || priceDisplayMode === 'lowest') {
      // null = Central says nothing is bookable; undefined = this payload lacks the field.
      const lowestCandidate = property.datesPrice?.lowest ?? property.lowestPrice
      if (priceDisplayMode === 'lowest' && unavailableLabel && rateUnbookable(lowestCandidate)) return renderUnavailable()
      const p = priceDisplayMode === 'lowest' ? (lowestCandidate ?? property.price) : property.price
      if (!(p > 0)) return null
      const label = priceDisplayMode === 'lowest' ? 'From' : 'Starting at'
      return (
        <div className="hk-margin-small-top hfse-property-price-container">
          <span className="hfse-property-price">{label} <strong>{formatPrice(p)}</strong></span>
        </div>
      )
    }

    // Full price loading skeleton
    if (quoteLoading) {
      return (
        <div className="hk-margin-small-top hfse-property-price-container">
          <span className="hfse-property-price hfse-property-price-loading">
            <svg width="100" height="14" viewBox="0 0 100 14">
              <defs>
                <linearGradient id={`price-grad-${property.id}`} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#eee" />
                  <stop offset="50%" stopColor="#ddd" />
                  <stop offset="100%" stopColor="#eee" />
                  <animate attributeName="x1" values="-1;1" dur="1.5s" repeatCount="indefinite" />
                  <animate attributeName="x2" values="0;2" dur="1.5s" repeatCount="indefinite" />
                </linearGradient>
              </defs>
              <rect width="100" height="14" rx="3" fill={`url(#price-grad-${property.id})`} />
            </svg>
          </span>
        </div>
      )
    }

    // Full price failed
    if (quoteFailed) {
      return (
        <div className="hk-margin-small-top hfse-property-price-container">
          <span className="hfse-property-price full-price-failed-text">View Full Price</span>
        </div>
      )
    }

    // Full price data available (with dates)
    if (quoteData) {
      const { rateplan, nights } = quoteData
      // #1064 toolkit rounding-strike-guard: same gate as grid-quote-price.tsx
      // (common/rounding-strike-guard.ts) — the two decision trees must stay
      // behaviorally identical per this file's own docblock / #895 / #902.
      const hasDiscount = isRoundingStrikeWorthShowing(rateplan.discount, nights)
      const afterTax = options.fullPriceAfterTax === true
      const taxDeduction = afterTax ? 0 : rateplan.totalTaxes
      const taxNote = afterTax ? <>including taxes &amp; fees</> : <>excluding taxes</>
      const totalPrice = rateplan.total - taxDeduction + Math.abs(rateplan.discount)
      const totalPriceDiscounted = rateplan.total - taxDeduction
      const avgPrice = totalPrice / nights
      const avgPriceDiscounted = totalPriceDiscounted / nights
      // #1691 toolkit original-total: struck pre-discount total beside the total in
      // the modes that print it unstruck (total mode already strikes it).
      const originalTotal = options.showOriginalTotal === true ? originalTotalOf(totalPriceDiscounted, rateplan.discount, nights) : null
      const struckTotal = originalTotal !== null && <><del data-original-total>{formatPrice(originalTotal)}</del>{' '}</>

      if (priceDisplayMode === 'total') {
        return (
          <div className="hk-margin-small-top hfse-property-price-container">
            <span className="hfse-property-price">
              {hasDiscount && <del>{formatPrice(totalPrice)}</del>}
              {' '}<strong>{formatPrice(totalPriceDiscounted)}</strong> total
            </span>
            <div className="hk-text hk-text-small hfse-property-tax-note">
              {nights} {nights === 1 ? 'night' : 'nights'} · {taxNote}
            </div>
          </div>
        )
      }

      if (priceDisplayMode === 'average') {
        return (
          <div className="hk-margin-small-top hfse-property-price-container">
            <span className="hfse-property-price">
              {hasDiscount && <del>{formatPrice(avgPrice)}</del>}
              {' '}<strong>{formatPrice(avgPriceDiscounted)}</strong> avg/night
            </span>
            <span className="hfse-property-price-sep"> &middot; </span>
            <span className="hfse-property-price-total">
              {struckTotal}{formatPrice(totalPriceDiscounted)} total
            </span>
            <div className="hk-text hk-text-small hfse-property-tax-note">
              {nights} {nights === 1 ? 'night' : 'nights'} · {taxNote}
            </div>
          </div>
        )
      }

      // per_night (default)
      return (
        <div className="hk-margin-small-top hfse-property-price-container">
          <span className="hfse-property-price">
            {hasDiscount && <del>{formatPrice(avgPrice)}</del>}
            {' '}<strong>{formatPrice(avgPriceDiscounted)}</strong> / night
          </span>
          <span className="hfse-property-price-sep"> &middot; </span>
          <span className="hfse-property-price-total">
            {struckTotal}{formatPrice(totalPriceDiscounted)} total
          </span>
          <div className="hk-text hk-text-small hfse-property-tax-note">
            {taxNote}
          </div>
        </div>
      )
    }

    // Dates queried but no full-price quote (fullPrice off) — use the calendar-derived
    // per-stay price (#2565): pre-tax total / avg-per-night over the requested window.
    if (isDateQuery && property.datesPrice && (property.datesPrice.total ?? 0) > 0) {
      const dp = property.datesPrice
      const nights = dp.nights
      const avg = dp.average ?? Math.round((dp.total ?? 0) / Math.max(nights, 1))
      const nightsNote = (
        <div className="hk-text hk-text-small hfse-property-tax-note">
          {nights} {nights === 1 ? 'night' : 'nights'} · excluding taxes &amp; fees
        </div>
      )

      if (priceDisplayMode === 'total') {
        return (
          <div className="hk-margin-small-top hfse-property-price-container">
            <span className="hfse-property-price"><strong>{formatPrice(dp.total ?? 0)}</strong> total</span>
            {nightsNote}
          </div>
        )
      }

      return (
        <div className="hk-margin-small-top hfse-property-price-container">
          <span className="hfse-property-price">
            <strong>{formatPrice(avg)}</strong>{priceDisplayMode === 'average' ? ' avg/night' : ' / night'}
          </span>
          <span className="hfse-property-price-sep"> &middot; </span>
          <span className="hfse-property-price-total">{formatPrice(dp.total ?? 0)} total</span>
          {nightsNote}
        </div>
      )
    }

    // Dates queried, no quote coming, no per-stay calendar data AND no forward-window
    // rate either: nothing bookable — label rather than the base rate. The
    // forward-window check matters: Recommendations cards are fetched WITHOUT the
    // visitor's dates (so Central never attaches datesPrice) but rendered with the
    // dated filters; an absent datesPrice alone is not an unavailability signal.
    if (isDateQuery && !quotePending && unavailableLabel
      && rateUnbookable(property.lowestPrice) && rateUnbookable(property.averagePrice)) return renderUnavailable()

    // No dates / no per-stay quote data (base & lowest handled early above).
    // `average` (no-dates) uses the calendar-derived average nightly (WP: average_rent).
    if (priceDisplayMode === 'average') {
      if (!quotePending && unavailableLabel && rateUnbookable(property.averagePrice)) return renderUnavailable()
      const avg = property.averagePrice ?? property.price
      if (avg > 0) {
        return (
          <div className="hk-margin-small-top hfse-property-price-container">
            <span className="hfse-property-price"><strong>{formatPrice(avg)}</strong> avg/night</span>
          </div>
        )
      }
    }

    // Ultimate fallback (e.g. total/per_night selected but no quote/calendar data): base nightly.
    if (property.price > 0) {
      return (
        <div className="hk-margin-small-top hfse-property-price-container">
          <span className="hfse-property-price">Starting at <strong>{formatPrice(property.price)}</strong></span>
        </div>
      )
    }

    return null
  }

  return (
    <div
      className="property-item hfse-property-container"
      data-id={property.id}
      onMouseEnter={() => mapActions?.focusMarker(property.id)}
      onMouseLeave={() => mapActions?.blurMarker(property.id)}
      {...compareRootProps(compare)}
    >
      {compare ? <CompareMark compare={compare} /> : null}
      {showFavoriteButton && (
        <button
          type="button"
          className={`hfse-property-favorite-toggle-btn ${favorited ? 'favorite' : ''}`}
          onClick={handleFavoriteToggle}
          aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill={favorited ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
      )}

      <a
        className="hk-card hk-card-default hk-card-hover hk-card-small hk-link-toggle hk-flex hk-flex-column hfse-property-card"
        href={propertyUrl}
        onClick={handleCardClick}
        target={card.openInNewTab ? '_blank' : undefined}
        rel={propertyLinkRel(propertyLink, card.openInNewTab)}
        {...(compare ? { tabIndex: -1, 'aria-hidden': true as const } : {})}
      >

        {/* Groups / Labels. Label Designer (#1069): with override rows configured the
            card badges every configured term with its cascaded inline style; without
            any, the legacy single primary-tag badge below is unchanged. */}
        {card.showGroups !== false && (labelConfig ? (
          inlineLabels.length > 0 && (
            <div className="hfse-property-labels hfse-property-groups">
              {tierInLabelsRow && <CardTierBadge property={property} config={tierRating} at="media" hasMedia />}
              {inlineLabels.map(renderBadge)}
            </div>
          )
        ) : (property.tag && (
          <div className="hfse-property-labels hfse-property-groups">
            {tierInLabelsRow && <CardTierBadge property={property} config={tierRating} at="media" hasMedia />}
            <span className="hfse-property-group">{property.tag}</span>
          </div>
        )))}

        {/* Media / Slideshow */}
        {images.length > 0 && (
          <div className="hk-card-media-top hfse-property-media">
            <div
              className="hk-position-relative hk-visible-toggle hk-light hfse-property-slideshow"
              tabIndex={-1}
              style={{ aspectRatio: card.imageAspectRatio ?? '16/9' }}
            >
              <ul className="hk-slideshow-items" style={{ position: 'relative', overflow: 'hidden', width: '100%', height: '100%' }}>
                {images.map((img, i) => (
                  <li
                    key={i}
                    className="hfse-property-slideshow-item"
                    style={slideStyle(i === currentImageIndex)}
                  >
                    {/* Only the slide on show is described, as in CardMedia, and
                        only by the feed's alt text: the photo sits inside the
                        card link, which already says the title — a title
                        fallback, or one alt per hidden slide, repeats it in
                        the link's name (#1865). */}
                    <img
                      src={getProxiedImageUrl(img, imgW, imgH)}
                      className="hfse-property-slideshow-image"
                      alt={i === currentImageIndex ? property.altText || '' : ''}
                      aria-hidden={i === currentImageIndex ? undefined : true}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </li>
                ))}
              </ul>

              {/* Overlay badges (#1370): inside the slideshow (position:relative,
                  explicit aspect-ratio) so bottom corners anchor to the PHOTO —
                  the box the toolkit's labels-frontend.js shim moves its row into.
                  On a multi-photo card a bottom corner rises above the slide-dot
                  strip (slice 2) so badges and dots stop sharing it. */}
              {card.showGroups !== false && overlayGroups.length > 0 && (
                <div className="hfse-property-labels-overlay">
                  {overlayGroups.map((g) => (
                    <div key={g.key} className={`hfse-label-overlay-group hfse-label-overlay-${g.corner}`} style={overlayGroupStyle(g, hasMultipleImages ? LABEL_OVERLAY_DOTNAV_RESERVE : 0)}>
                      {g.labels.map(renderBadge)}
                    </div>
                  ))}
                </div>
              )}

              {!tierInLabelsRow && <CardTierBadge property={property} config={tierRating} at="media" hasMedia />}

              {hasMultipleImages && (
                <>
                  <span
                    className="hk-position-center-left hk-position-small hk-hidden-hover hfse-property-slideshow-prev-btn"
                    onClick={handlePrevImage}
                    role="button"
                    aria-label="Previous"
                  />
                  <span
                    className="hk-position-center-right hk-position-small hk-hidden-hover hfse-property-slideshow-next-btn"
                    onClick={handleNextImage}
                    role="button"
                    aria-label="Next"
                  />
                  <ul className="hk-slideshow-nav hk-dotnav hk-flex-center hk-position-bottom hk-margin-small-bottom hfse-property-slideshow-dotnav">
                    {dotWindow(images.length, currentImageIndex, MAX_DOTS).map((i) => (
                      <li key={i} className={i === currentImageIndex ? 'hk-active' : ''}>
                        {/* Named like the Template card's dots (CardMedia); English
                            like this card's Previous/Next — the legacy card stays out
                            of the translation tables on purpose (explorer/translations.ts). */}
                        <button
                          type="button"
                          aria-label={`Photo ${i + 1} of ${images.length}`}
                          aria-current={i === currentImageIndex || undefined}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setCurrentImageIndex(i)
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        )}

        {/* Card Body */}
        <div className="hk-card-body hk-margin-remove-first-child hfse-property-body">
          <div className="hk-flex hk-flex-between hk-flex-top">
            <h3 className="hk-h5 hk-flex-auto hfse-property-title">
              {property.title}
            </h3>
            {renderRating('title')}
          </div>
          <CardTierBadge property={property} config={tierRating} at="title" hasMedia={images.length > 0} />
          {subtitleText && (
            <div className="hfse-property-subtitle">{subtitleText}</div>
          )}
          {renderPrice()}
        </div>

        {/* Meta */}
        <div className="hfse-property-meta">
          {(card.showLocation !== false || card.showType !== false) && (
          <div className={tagOwnRow ? 'hfse-property-meta-primary hfse-card-tags-own-row' : 'hfse-property-meta-primary'}>
            {card.showLocation !== false && !!leftTerms && (
            <div className="terms terms-left hfse-property-locations">
              {leftIcon ? (
                <i className={leftIcon} aria-hidden="true" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              )}
              {leftTerms}
            </div>
            )}
            {showTagSlot && (
            <div className="terms terms-right hfse-property-types hfse-property-tags">
              {tagPills
                ? cardTags.map((t) => <span key={t.key} className="hfse-card-tag" style={tagPillStyle}>{t.text}</span>)
                : cardTags.map((t) => t.text).join(' / ')}
            </div>
            )}
            {showTypeSlot && (
            <div className="terms terms-right hfse-property-types">
              {showTypeIcon && (rightIcon ? (
                <i className={rightIcon} aria-hidden="true" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
              ))}
              {rightTerms}
            </div>
            )}
          </div>
          )}
          {renderRating('location')}
          {card.showFeatures !== false && (
          <div className="hfse-property-meta-secondary">
            <div className="hfse-property-guests">
              {!specText && <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
              {specText ? `Sleeps ${property.features.guests}` : property.features.guests}
            </div>
            <div className="hfse-property-bedrooms">
              {!specText && <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/></svg>}
              {/* "Studio" is already a label — the toolkit leaves it alone too. */}
              {specText && bedsLabel !== 'Studio' ? `${bedsLabel} BR` : bedsLabel}
            </div>
            <div className="hfse-property-bathrooms">
              {!specText && <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l0 12"/><path d="M3 12h18"/><path d="M6 2v2a3 3 0 0 0 3 3"/><path d="M4 22h16"/><path d="M4 12v6a2 2 0 0 0 2 2h0"/><path d="M20 12v6a2 2 0 0 1-2 2h0"/></svg>}
              {specText ? `${property.features.baths} BA` : property.features.baths}
            </div>
            {/* Unit Grouping (#1070): minimal secondary-meta indicator, same
                row/markup pattern as the guests/beds/baths chips above — no
                new layout. Only the representative row carries unit_group. */}
            {property.unit_group && property.unit_group.size > 0 && (
              <div className="hfse-property-unit-group">
                {property.unit_group.size} units
              </div>
            )}
          </div>
          )}
        </div>
      </a>
    </div>
  )
}
